import UIDefaultTheme from 'src/themes';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  AbstractControl,
  Validators,
} from '@angular/forms';
import {
  applyDocumentMask,
  formatDocumentForDisplay,
  getDocumentTypeLabel,
  isCnpjDocument,
  isCpfDocument,
  isDocumentComplete,
  maskCnpj,
  maskCpf,
  maskDocument,
  normalizeDocumentForApi,
  unmaskDocument,
  validateCnpj,
} from './document';

export {
  applyDocumentMask,
  CNPJ_MASK,
  CPF_MASK,
  DOCUMENT_MASK,
  formatDocumentForDisplay,
  getDocumentTypeLabel,
  isCnpjDocument,
  isCpfDocument,
  isDocumentComplete,
  maskCnpj,
  maskCpf,
  maskDocument,
  normalizeDocumentForApi,
  unmaskDocument,
  validateCnpj,
} from './document';

export class BrValidators {
  static telefone(tel: any) {
    const telClean = tel.replace(/[^\d]+/g, '');
    tel = tel.replace(/_/g, '');
    if (!(telClean.length === 10 || telClean.length === 11)) {
      return false;
    }
    if (telClean[0] == 0 || telClean[2] == 0) {
      return false;
    }
    return true;
  }

  static telefoneFixo(tel: any) {
    if (!tel) {
      return false;
    }
    const telClean = tel.replace(/[^\d]+/g, '').replace(/_/g, '');
    // Telefone fixo deve ter exatamente 10 dígitos (DDD + 8 dígitos, sem o 9)
    if (telClean.length !== 10) {
      return false;
    }
    // DDD não pode começar com 0
    if (telClean[0] == 0 || telClean[2] == 0) {
      return false;
    }
    return true;
  }

  static celular(cel: any) {
    let celClean = cel.replace(/[^\d]+/g, '');
    celClean = celClean.replace(/_/g, '');
    if (celClean.length !== 11) {
      return false;
    }
    if (celClean[0] == 0 || celClean[2] < 5) {
      return false;
    }
    return true;
  }

  static cep(cep: string) {
    const cepClean = cep.replace(/[^\d]+/g, '');
    const exp = /\d{2}\.\d{3}\-\d{3}/;
    if (!exp.test(cep) && cepClean.length !== 8) {
      return false;
    }
    return true;
  }

  static modulo11Custom = (
    string: string,
    size: number,
    maxMult = string.length,
    by10 = true
  ) => {
    if (!by10) size = 1;
    for (let n = 1; n <= size; n++) {
      let soma = 0;
      let mult = 2;
      for (let i = string.length - 1; i >= 0; i--) {
        soma += mult * parseInt(string.charAt(i));
        mult++;
        if (mult > maxMult) mult = 2;
      }
      let dig;
      if (by10) {
        dig = ((soma * 10) % 11) % 10;
      } else {
        dig = soma % 11;
        if (dig == 10) dig = 0;
      }
      string += dig;
    }
    return string.substr(string.length - size, size);
  };

  static create_cpf(strCPF: string) {
    strCPF = strCPF.replace(/[^\d]+/g, '');
    if (strCPF === '00000000000') {
      return false;
    }

    const restoscustom = BrValidators.modulo11Custom(
      strCPF.substr(0, strCPF.length - 2),
      2,
      12
    );
    return restoscustom;
  }

  static cpf(strCPF: any) {
    // Valida se tem apenas número, - ou .
    let precisaFicarVazio = strCPF.replace(/^[0-9.-]*$/gm, '');
    if (precisaFicarVazio != '') return false;

    strCPF = strCPF.replace(/[^\d]+/g, '');
    if (strCPF.length !== 11) {
      return false;
    }

    //verifica se todos os numeros sao iguais
    const expIguais = /^(?!.*(\d)\1{10}).*$/;
    if (!strCPF.match(expIguais)) {
      return false;
    }

    // valida digito verificados
    const restos = BrValidators.create_cpf(strCPF);
    if (!restos || restos != strCPF.substr(-2)) {
      return false;
    }
    return true;
  }

  static create_cnpj(cnpj: string) {
    const filteredCnpj = unmaskDocument(cnpj);
    if (!validateCnpj(filteredCnpj)) {
      return false;
    }

    const digitos = filteredCnpj.substring(12);
    return [parseInt(digitos.charAt(0), 10), parseInt(digitos.charAt(1), 10)];
  }

  static cnpj(cnpj: any) {
    if (typeof cnpj !== 'string' && typeof cnpj !== 'number') {
      return false;
    }

    return validateCnpj(String(cnpj));
  }
}
export class UtilsValidators {
  static isPresent(obj: any): boolean {
    return obj !== undefined && obj !== null;
  }

  static telefone(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    return BrValidators.telefone(v) ? null : { telefone: true };
  }

  static telefoneFixo(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    if (!control.value) {
      return null; // Se vazio, deixa o required validator tratar
    }

    try {
      const v: string = control.value;
      return BrValidators.telefoneFixo(v) ? null : { telefoneFixo: true };
    } catch (error) {
      // Não trava o sistema se houver erro na validação
      console.error('Erro na validação de telefone fixo:', error);
      return null;
    }
  }

  static celular(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    return BrValidators.celular(v) ? null : { celular: true };
  }

  static celularWithDDI(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    if (!v || typeof v !== 'string') {
      return { celular: true };
    }

    // Remove caracteres não numéricos
    const numbersOnly = v.replace(/[^\d]/g, '');

    // Acessa o formulário pai para pegar o DDI
    const parent = control.parent;
    if (!parent) {
      return { celular: true };
    }

    const ddiControl = parent.get('cellphone_ddi');
    const ddi = ddiControl ? ddiControl.value : null;
    const ddiNumber = ddi ? ddi.toString().replace(/[^\d]/g, '') : '';

    // Verifica se o número começa com 55
    if (numbersOnly.startsWith('55')) {
      // Se o DDI também for 55, precisa verificar se é DDI duplicado ou DDD
      if (ddiNumber === '55') {
        // Se o número tem 13 ou mais dígitos, pode ser DDI duplicado
        // Formato suspeito: 55 (DDI) + DDD (2) + número (8 ou 9) = 13 ou 14 dígitos
        if (numbersOnly.length >= 13) {
          const after55 = numbersOnly.substring(2);
          // Verifica se depois do 55 tem um DDD válido (2 dígitos, não pode começar com 0)
          const possibleDDD = after55.substring(0, 2);
          if (possibleDDD && possibleDDD[0] !== '0' && possibleDDD.length === 2) {
            // Verifica se o resto tem 8 ou 9 dígitos (número do celular)
            const rest = after55.substring(2);
            if (rest.length === 8 || rest.length === 9) {
              // É DDI duplicado: 55 (DDI) + DDD + número
              return { celularDDIDuplicated: true };
            }
          }
        }
        // Se tem 10 ou 11 dígitos começando com 55 e DDI é 55, então o 55 é o DDD (OK)
      }
      // Se o DDI não for 55, então o 55 no número é o DDD (OK)
    }

    // Valida formato: DDD (2 dígitos) + número (8 ou 9 dígitos)
    // Total: 10 ou 11 dígitos
    if (numbersOnly.length !== 10 && numbersOnly.length !== 11) {
      return { celular: true };
    }

    // Verifica se começa com DDD válido (2 dígitos, não pode começar com 0)
    const ddd = numbersOnly.substring(0, 2);
    if (ddd[0] === '0') {
      return { celular: true };
    }

    // Verifica se o número não é apenas dígitos repetidos
    // Ex: 0000000000, 1111111111, 2222222222, etc.
    const allSame = /^(\d)\1+$/.test(numbersOnly);
    if (allSame) {
      return { celularRepeated: true };
    }

    // Valida formato de celular brasileiro
    if (numbersOnly.length === 11) {
      // Formato com 9: DDD (2) + 9 + número (8 dígitos)
      // Exemplo: 13981471536 (13 DDD + 9 + 81471536)
      if (numbersOnly[2] !== '9') {
        return { celular: true };
      }
      // Verifica se o primeiro dígito do número (índice 3) é >= 5
      if (parseInt(numbersOnly[3]) < 5) {
        return { celular: true };
      }
    } else if (numbersOnly.length === 10) {
      // Formato sem 9: DDD (2) + número (8 dígitos)
      // Exemplo: 1381471536 (13 DDD + 81471536)
      // Verifica se o primeiro dígito do número (índice 2) é >= 5
      if (parseInt(numbersOnly[2]) < 5) {
        return { celular: true };
      }
    }

    return null;
  }

  static cep(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    return BrValidators.cep(v) ? null : { cep: true };
  }

  static email(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    if (!v || typeof v !== 'string') {
      return { email: true };
    }

    // Verifica se tem @
    if (!v.includes('@')) {
      return { email: true };
    }

    // Divide em partes antes e depois do @
    const parts = v.split('@');
    if (parts.length !== 2) {
      return { email: true };
    }

    const [localPart, domain] = parts;

    // Verifica se a parte local não está vazia
    if (!localPart || localPart.trim().length === 0) {
      return { email: true };
    }

    // Verifica se o domínio não está vazio
    if (!domain || domain.trim().length === 0) {
      return { email: true };
    }

    // Verifica se o domínio termina com ponto (ex: @gmail.)
    if (domain.endsWith('.')) {
      return { email: true };
    }

    // Verifica se o domínio tem pelo menos um ponto e termina com extensão válida
    // Ex: @gmail.com.b é inválido (termina com .b que é muito curto)
    const domainParts = domain.split('.');
    if (domainParts.length < 2) {
      return { email: true };
    }

    // Verifica se a última parte do domínio tem pelo menos 2 caracteres
    const tld = domainParts[domainParts.length - 1];
    if (!tld || tld.length < 2) {
      return { email: true };
    }

    // Validação básica de formato de email
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(v)) {
      return { email: true };
    }

    return null;
  }

  static cpf(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    return BrValidators.cpf(v) ? null : { cpf: true };
  }

  static cnpj(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    return BrValidators.cnpj(v) ? null : { cnpj: true };
  }

  static fullName(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    if (!v || typeof v !== 'string') {
      return { fullName: true };
    }

    // Verifica se contém números
    if (/\d/.test(v)) {
      return { fullNameNumbers: true };
    }

    // Remove espaços extras e divide em palavras
    const words = v.trim().split(/\s+/).filter(word => word.length > 0);

    // Verifica se tem no mínimo 2 nomes
    if (words.length < 2) {
      return { fullNameIncomplete: true };
    }

    // Lista de saudações e títulos comuns
    const titles = [
      'sr', 'sra', 'srta', 'miss', 'mr', 'mrs', 'ms',
      'dr', 'dra', 'prof', 'profa', 'eng', 'enga',
      'adv', 'adva', 'dep', 'sen', 'pref', 'prefa'
    ];

    // Verifica cada palavra
    for (const word of words) {
      const wordLower = word.toLowerCase().replace(/\./g, '');

      // Verifica se é uma saudação/título
      if (titles.includes(wordLower)) {
        return { fullNameTitle: true };
      }

      // Verifica se é uma abreviação:
      // - Uma letra seguida de ponto (ex: "P.", "G.", "s.")
      // - Apenas uma letra (ex: "P", "G", "s")
      // - Uma letra seguida de ponto e espaço ou fim da string
      const trimmedWord = word.trim();
      if (trimmedWord.length === 1 || (trimmedWord.length === 2 && trimmedWord.endsWith('.'))) {
        return { fullNameAbbreviated: true };
      }
    }

    return null;
  }

  static cpfOrCnpj(control: AbstractControl) {
    if (UtilsValidators.isPresent(Validators.required(control))) {
      return null;
    }

    const v: string = control.value;
    if (!v) {
      return null;
    }

    const unmasked = unmaskDocument(v);
    const hasLetters = /[A-Z]/.test(unmasked.slice(0, 12));
    const digitsOnly = v.replace(/[^\d]+/g, '');

    if (hasLetters) {
      if (unmasked.length !== 14) {
        return { invalidDocument: true };
      }
      const isValidCnpj = BrValidators.cnpj(v);
      return isValidCnpj ? null : { cnpj: true };
    }

    if (digitsOnly.length === 11) {
      const isValidCpf = BrValidators.cpf(v);
      return isValidCpf ? null : { cpf: true };
    }

    if (digitsOnly.length === 14) {
      const isValidCnpj = BrValidators.cnpj(v);
      return isValidCnpj ? null : { cnpj: true };
    }

    if (digitsOnly.length > 0 || unmasked.length > 0) {
      return { invalidDocument: true };
    }

    return null;
  }

  static notOnlyWhitespace(control: AbstractControl) {
    if (!control.value) {
      return null; // Se está vazio, deixa o required validator tratar
    }

    const v: string = control.value;
    if (typeof v === 'string' && v.trim().length === 0) {
      return { notOnlyWhitespace: true };
    }

    return null;
  }

  static noSpaces(control: AbstractControl) {
    if (!control.value) {
      return null; // Se está vazio, deixa o required validator tratar
    }

    const v: string = control.value;
    if (typeof v === 'string' && /\s/.test(v)) {
      return { noSpaces: true };
    }

    return null;
  }

  static emailValidator(control: AbstractControl): ValidationErrors | null {
    try {
      if (!control || !control.value) {
        return null; // Let required validator handle empty values
      }

      // Verifica se o valor é uma string
      if (typeof control.value !== 'string') {
        return null; // Deixa outros validators tratar valores não-string
      }

      const value: string = control.value.trim().toLowerCase();

      // Se após trim estiver vazio, deixa o required validator tratar
      if (!value) {
        return null;
      }

      // Verifica se tem @
      if (!value.includes('@')) {
        return { invalidEmailFormat: true };
      }

      const parts = value.split('@');
      if (parts.length !== 2) {
        return { invalidEmailFormat: true };
      }

      const domain = parts[1];

      // Verifica se o domínio tem pelo menos um ponto
      if (!domain || !domain.includes('.')) {
        return { invalidEmailFormat: true };
      }

      // Verifica se termina com ponto seguido de uma letra (ex: .x, .c)
      const lastPart = domain.split('.').pop();
      if (lastPart && lastPart.length === 1) {
        return { invalidEmailFormat: true };
      }

      // Verifica se o domínio tem pelo menos 2 caracteres após o último ponto
      if (lastPart && lastPart.length < 2) {
        return { invalidEmailFormat: true };
      }

      return null;
    } catch (error) {
      // Em caso de erro, retorna null para não quebrar o formulário
      console.warn('Erro no validador de email:', error);
      return null;
    }
  }

  static urlValidator(control: AbstractControl): ValidationErrors | null {
    try {
      if (!control || !control.value) {
        return null; // Let required validator handle empty values
      }

      // Verifica se o valor é uma string
      if (typeof control.value !== 'string') {
        return null; // Deixa outros validators tratar valores não-string
      }

      const value: string = control.value.trim();

      // Se após trim estiver vazio, deixa o required validator tratar
      if (!value) {
        return null;
      }

      // Remove espaços (já validado por noSpaces, mas garante aqui também)
      if (/\s/.test(value)) {
        return { invalidUrlFormat: true };
      }

      // Pattern para URL: aceita com ou sem protocolo (http://, https://, www., etc)
      // Aceita domínios como: exemplo.com, www.exemplo.com, http://exemplo.com, https://www.exemplo.com
      const urlPattern =
        /^(https?:\/\/)?(www\.)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;

      if (!urlPattern.test(value)) {
        return { invalidUrlFormat: true };
      }

      return null;
    } catch (error) {
      // Em caso de erro, retorna null para não quebrar o formulário
      console.warn('Erro no validador de URL:', error);
      return null;
    }
  }
}
export default class Utils {
  static lastMessage: string = '';

  static noEmptyFields(obj: any): any {
    const updated = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        if (obj[key].length > 0) {
          updated[key] = obj[key];
        }
      } else {
        if (obj[key] !== '' && obj[key] !== undefined && obj[key] !== null) {
          updated[key] = obj[key];
        }
      }
    }

    return updated;
  }

  static makeID(length: number = 16) {
    var result = '';
    var characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  static convertToSlug(text: string) {
    let str = text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/^\s+|\s+$/g, '');
    str = str.toLowerCase();

    const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
    const to = 'aaaaeeeeiiiioooouuuunc------';
    for (let i = 0, l = from.length; i < l; i++) {
      str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }

    str = str
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

    return str;
  }

  static convertBRdateToUSdate(date: string | null | undefined) {
    if (date) {
      const dateArray = date.split('/');
      const reversedDateArray = dateArray.reverse();
      return reversedDateArray.join('-');
    }
    return null;
  }

  /** Apenas dígitos — usar em CPF, telefone e CEP. Não usar em CNPJ (use unmaskDocument). */
  static onlyNumbers(str: string) {
    if (str) {
      return String(str).replace(/[^\d]/g, '');
    }
    return '';
  }

  static unmaskDocument = unmaskDocument;
  static normalizeDocumentForApi = normalizeDocumentForApi;
  static formatDocumentForDisplay = formatDocumentForDisplay;
  static isCnpjDocument = isCnpjDocument;
  static isCpfDocument = isCpfDocument;
  static isDocumentComplete = isDocumentComplete;
  static maskDocument = maskDocument;
  static maskCnpj = maskCnpj;
  static maskCpf = maskCpf;
  static getDocumentTypeLabel = getDocumentTypeLabel;
  static applyDocumentMask = applyDocumentMask;

  static onlyLetters(str: string) {
    if (str) {
      return String(str).replace(/[^a-zA-Z]/g, '');
    }
    return '';
  }

  static removeSpecialChars(str: string) {
    if (str) {
      return String(str).replace(/[^a-zA-Z0-9]/g, '');
    }
    return '';
  }

  static formatBrl(value: number) {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  static brlToNumber(value: string | undefined) {
    if (value && typeof value === 'string') {
      if (value.includes('R$')) {
        value = value.split('R$').join('');
      }

      if (value.includes('.')) {
        value = value.split('.').join('');
      }

      if (value.includes(',')) {
        value = value.split(',').join('.');
      }

      value = value.split(' ').join('');

      return Number(value);
    }

    if (typeof value === 'number') {
      return value;
    }

    return 0;
  }

  static todayString() {
    return new Date().toISOString().split('T')[0];
  }

  static tomorrowString() {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  }

  static validDate() {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 25);

    return today.toISOString().split('T')[0];
  }

  static getErrors(form: any): string[] {
    const errors: string[] = [];
    if (form) {
      Object.keys(form.controls).forEach((key) => {
        const controlErrors: ValidationErrors | null | undefined =
          form?.get(key)?.errors;
        if (controlErrors != null) {
          Object.keys(controlErrors).forEach((keyError) => {
            errors.push(key.replace('_', ' ') + ' ' + keyError);
          });
        }
      });
    }
    return errors;
  }

  static validateWhenNotEmpty(validator: ValidatorFn) {
    return function (control: FormControl) {
      if (control.value) {
        return validator(control);
      }

      return null;
    };
  }

  static validateIfDirty(validator: ValidatorFn) {
    return function (control: FormControl) {
      const form: any = control.parent;

      if (form) {
        const isDirty = Object.keys(form.controls).some((key) => {
          if (form.get(key).dirty) {
            return true;
          }
          return false;
        });

        if (isDirty) {
          return validator(control);
        }

        return null;
      }
      return null;
    };
  }

  static ageValidation(control: FormControl) {
    if (control.value) {
      const date = new Date(control.value);
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const month = today.getMonth() - date.getMonth();
      const day = today.getDate() - date.getDate();

      if (month < 0 || (month === 0 && day < 0)) {
        age--;
      }

      if (age < 18) {
        return { age: true };
      }
    }

    return null;
  }

  static validateDateValid(control: FormControl) {
    if (control.value && control.value.length == 10) {
      const date = control.value.split('/');

      if (date.length != 3) {
        return { date: true };
      }

      const day = date[0];
      const month = date[1];
      const year = date[2];

      if (day && month && year) {
        const date = new Date(year, month - 1, day);

        if (
          date.getFullYear() != year ||
          date.getMonth() != month - 1 ||
          date.getDate() != day
        ) {
          return { date: true };
        }

        if (date.toString() == 'Invalid Date') {
          return { date: true };
        }

        return null;
      }
    }

    return { date: true };
  }

  static validateLegalAge(control: FormControl) {
    const dataInvalid = Utils.validateDateValid(control);

    if (dataInvalid === null) {
      const dateArr = control.value.split('/');

      const dateDay = dateArr[0];
      const dateMonth = dateArr[1];
      const dateYear = dateArr[2];

      const date = new Date(dateYear, dateMonth - 1, dateDay);

      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const month = today.getMonth() - date.getMonth();
      const day = today.getDate() - date.getDate();

      if (month < 0 || (month === 0 && day < 0)) {
        age--;
      }

      if (age < 18) {
        return { age: true };
      }
    }

    return null;
  }

  static validateMinDate(control: FormControl) {
    const dataInvalid = Utils.validateDateValid(control);

    if (dataInvalid === null) {
      const dateArr = control.value.split('/');

      const dateDay = dateArr[0];
      const dateMonth = dateArr[1];
      const dateYear = dateArr[2];

      const date = new Date(dateYear, dateMonth - 1, dateDay);

      const year = date.getFullYear();

      const minDate = new Date(`01/01/1900`);

      if (date < minDate) {
        return { min: true };
      }

      return null;
    }

    return dataInvalid;
  }

  static validateMaxDate(control: FormControl) {
    const dataInvalid = Utils.validateDateValid(control);

    if (dataInvalid === null) {
      const dateArr = control.value.split('/');

      const dateDay = dateArr[0];
      const dateMonth = dateArr[1];
      const dateYear = dateArr[2];

      const date = new Date(dateYear, dateMonth - 1, dateDay);

      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setFullYear(today.getFullYear() + 100);

      if (date > maxDate) {
        return { max: true };
      }

      return null;
    }

    return dataInvalid;
  }

  static validateMaxMinToday(control: FormControl, max: boolean = true) {
    const dataInvalid = Utils.validateDateValid(control);

    if (dataInvalid === null) {
      const dateArr = control.value.split('/');

      const dateDay = dateArr[0];
      const dateMonth = dateArr[1];
      const dateYear = dateArr[2];

      const date = new Date(dateYear, dateMonth - 1, dateDay);
      const today = new Date();

      if (max) {
        if (date > today) {
          return { max: true };
        }
      } else {
        if (date < today) {
          return { min: true };
        }
      }

      return null;
    }

    return dataInvalid;
  }

  static validateMinConstitutionDate(referenceDate: string) {
    return (control: FormControl) => {
      const dataInvalid = Utils.validateDateValid(control);
      
      if (dataInvalid !== null || !referenceDate || !control.value) {
        return dataInvalid;
      }

      try {
        const dateArr = control.value.split('/');
        const dateDay = parseInt(dateArr[0]);
        const dateMonth = parseInt(dateArr[1]);
        const dateYear = parseInt(dateArr[2]);

        const refDateArr = referenceDate.split('/');
        const refDay = parseInt(refDateArr[0]);
        const refMonth = parseInt(refDateArr[1]);
        const refYear = parseInt(refDateArr[2]);

        // Verificar se os valores são válidos
        if (
          isNaN(dateDay) || isNaN(dateMonth) || isNaN(dateYear) ||
          isNaN(refDay) || isNaN(refMonth) || isNaN(refYear)
        ) {
          return null;
        }

        const date = new Date(dateYear, dateMonth - 1, dateDay);
        const refDate = new Date(refYear, refMonth - 1, refDay);

        // Verificar se as datas são válidas
        if (isNaN(date.getTime()) || isNaN(refDate.getTime())) {
          return null;
        }

        if (date < refDate) {
          return { minConstitutionDate: true };
        }

        return null;
      } catch (error) {
        return { date: true };
      }
    };
  }

  static verifyDateLessCurrentDate(currentDate: string, comparedDate: string) {
    const currentDateArr = currentDate.split('/');

    const currentDay = Number(currentDateArr[0]);
    const currentMonth = Number(currentDateArr[1]);
    const currentYear = Number(currentDateArr[2]);

    const comparedDateArr = comparedDate.split('/');

    const comparedDay = Number(comparedDateArr[0]);
    const comparedMonth = Number(comparedDateArr[1]);
    const comparedYear = Number(comparedDateArr[2]);

    const currentNewDate = new Date(currentYear, currentMonth - 1, currentDay);
    const comparedNewDate = new Date(
      comparedYear,
      comparedMonth - 1,
      comparedDay
    );

    return currentNewDate < comparedNewDate;
  }

  static validateOneOf(validators: ValidatorFn[]) {
    return function (control: FormControl) {
      const isValid = validators.some((validator) => {
        return validator(control) === null;
      });

      if (isValid) {
        return null;
      }

      return { invalid: true };
    };
  }

  static validateIfFilled(validator: ValidatorFn, filter: string[] = []) {
    return function (control: FormControl) {
      const form: any = control.parent;

      if (form) {
        const isFilled = Object.keys(form.controls)
          .filter((key) => filter.includes(key))
          .some((key) => {
            if (form.get(key).value !== null || form.get(key).value !== null) {
              return true;
            }

            return false;
          });

        if (isFilled) {
          return validator(control);
        }

        return null;
      }
      return null;
    };
  }

  static resetZipcodeFields = (form: any) => {
    form.controls.public_place.reset();
    form.controls.number.reset();
    form.controls.complement.reset();
    form.controls.district.reset();
    form.controls.city.reset();
    form.controls.uf.reset();
    form.controls.country.reset();
  };

  static validateCpfCnpj(controls: FormControl) {
    const validateCpf = UtilsValidators.cpf(controls);
    const validateCnpj = UtilsValidators.cnpj(controls);

    if (validateCpf === null || validateCnpj === null) {
      return null;
    }

    return { invalid: true };
  }

  static maxDigits(max: number) {
    return function (control: FormControl) {
      if (control.value) {
        const numbers = String(control.value).split('.');

        if (numbers[0].length > max) {
          return { maxDigits: true };
        }
      }

      return null;
    };
  }

  static maxNumber(max: number) {
    return function (control: FormControl) {
      if (control.value) {
        if (typeof control.value === 'string') {
          const num = Number(control.value);
          if (num > max) {
            return { maxNumber: true };
          }
        } else {
          if (control.value > max) {
            return { maxNumber: true };
          }
        }
      }

      return null;
    };
  }

  static setTitle(role: string) {
    let title = '';

    switch (role) {
      case 'ASSIGNOR-PF-PF':
        title = 'Cadastro Cedente Pessoa Física | ' + UIDefaultTheme.brand.name;
        break;
      case 'ASSIGNOR-PJ-FI':
        title =
          'Cadastro Cedente Fundo de Investimento | ' +
          UIDefaultTheme.brand.name;
        break;
      case 'ASSIGNOR-PJ-IF':
        title =
          'Cadastro Cedente Instituição Financeira | ' +
          UIDefaultTheme.brand.name;
        break;
      case 'ASSIGNOR-PJ-PJ':
        title =
          'Cadastro Cedente Pessoa Jurídica | ' + UIDefaultTheme.brand.name;
        break;
      case 'SHAREHOLDER-PJ-FI':
        title =
          'Cadastro Cotista Fundo de Investimento | ' +
          UIDefaultTheme.brand.name;
        break;
      case 'SHAREHOLDER-PJ-IF':
        title =
          'Cadastro Cotista Instituição Financeira | ' +
          UIDefaultTheme.brand.name;
        break;
      case 'SHAREHOLDER-PF-PFNR':
        title = 'Cadastro Cotista Pessoa Física | ' + UIDefaultTheme.brand.name;
        break;
      case 'SHAREHOLDER-PF-PFR':
        title = 'Cadastro Cotista Pessoa Física | ' + UIDefaultTheme.brand.name;
        break;
      case 'SHAREHOLDER-PJ-PJ':
        title =
          'Cadastro Cotista Pessoa Jurídica | ' + UIDefaultTheme.brand.name;
        break;
      case 'SHAREHOLDER-PJ-PJNR':
        title =
          'Cadastro Cotista Pessoa Jurídica | ' + UIDefaultTheme.brand.name;
        break;
      default:
        title = UIDefaultTheme.brand.name;
        break;
    }

    return title;
  }

  static getRoleFilterOptions(): { label: string; value: string }[] {
    return [
      { label: 'Cotista - Pessoa Física', value: 'cotista-pf' },
      { label: 'Cotista - Pessoa Jurídica', value: 'cotista-pj' },
      { label: 'Cotista - Fundo de investimento', value: 'cotista-fundo' },
      { label: 'Cedente - Pessoa Física', value: 'cedente-pf' },
      { label: 'Cedente - Pessoa Jurídica', value: 'cedente-pj' },
      { label: 'Cedente - Fundo de investimento', value: 'cedente-fundo' },
      { label: 'Prestador - Pessoa Física', value: 'prestador-pf' },
      { label: 'Prestador - Pessoa Jurídica', value: 'prestador-pj' },
    ];
  }

  static getApplicableFromRoleFilterValue(value?: string | null): string | null {
    const roleValue = String(value || '').toLowerCase();
    if (!roleValue) return null;
    if (roleValue.startsWith('cotista')) return 'shareholder';
    if (roleValue.startsWith('cedente')) return 'assignor';
    if (roleValue.startsWith('prestador')) return 'provider';
    return null;
  }

  static isPjFundRole(
    role?: { applicable?: string; slug?: string; name?: string } | null,
  ): boolean {
    const slug = String(role?.slug || '').toUpperCase();
    return slug.includes('PJ-FI');
  }

  static getRoleApplicableDisplayLabel(
    role?: {
      applicable?: string;
      slug?: string;
      name?: string;
      applicable_display?: string;
    } | null,
    fallback = '—',
  ): string {
    if (!role) return fallback;

    if (Utils.isPjFundRole(role)) {
      const applicable = String(role.applicable || '').toLowerCase();
      const slug = String(role.slug || '').toUpperCase();
      if (applicable === 'shareholder' || slug.includes('SHAREHOLDER')) {
        return 'Cotista Fundo de Investimento';
      }
      if (applicable === 'assignor' || slug.includes('ASSIGNOR')) {
        return 'Cedente Fundo de Investimento';
      }
    }

    const fromApi = String(role.applicable_display || '').trim();
    if (fromApi) return fromApi;

    const slug = String(role.slug || '').toUpperCase();
    if (slug.includes('SHAREHOLDER')) return 'Cotista';
    if (slug.includes('ASSIGNOR')) return 'Cedente';
    if (slug.includes('PROVIDER')) return 'Prestador de Serviço';
    if (slug.includes('REPRESENTATIVE')) return 'Representante';

    const applicable = String(role.applicable || '').toLowerCase();
    switch (applicable) {
      case 'shareholder':
        return 'Cotista';
      case 'assignor':
        return 'Cedente';
      case 'provider':
        return 'Prestador de Serviço';
      case 'representative':
        return 'Representante';
      default:
        break;
    }

    return String(role.name || fallback);
  }

  static filterRepeatedValues(array: any[], key: string) {
    const filteredArray = array.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t[key] === item[key])
    );

    return filteredArray;
  }

  static searchMessage(obj) {
    const searched = [];

    if (obj) {
      Object.keys(obj).forEach((key) => {
        if (key === 'message') {
          if (!searched.includes(obj[key])) {
            searched.push(obj[key]);
          }
        } else if (typeof obj[key] === 'object') {
          const found = Utils.searchMessage(obj[key]);

          if (found.length > 0) {
            found.forEach((item) => {
              if (!searched.includes(item)) {
                searched.push(item);
              }
            });
          }
        }
      });
    }

    return searched;
  }

  static whitespaceValidator(form: FormControl): ValidationErrors {
    return form.value && form.value.startsWith(' ')
      ? { whitespace: true }
      : null;
  }

  static getInitials(name: any) {
    const arr = name
      .toUpperCase()
      .replace(/[^\w\s]/gi, '')
      .split(' ');

    const range = arr.length - 1;
    let haveLastName = false;
    let lastNameindex = range;
    let firstNameIndex = 0;
    let lastNameChar = '';
    let firstNameChar = '';

    while (firstNameIndex <= range) {
      if (arr[firstNameIndex] !== '') {
        firstNameChar = arr[firstNameIndex][0];
        break;
      }

      firstNameIndex++;
    }

    while (lastNameindex !== firstNameIndex || haveLastName) {
      if (arr[lastNameindex] !== '') {
        lastNameChar = arr[lastNameindex][0];
        haveLastName = true;
        break;
      }

      lastNameindex--;
    }

    return firstNameChar + lastNameChar;
  }

  static consoleLog(message) {
    // Prevente equals last
    if (message !== this.lastMessage) {
      this.lastMessage = message;
    }
  }

  static renderValue(parentPath: string[], key, from: any) {
    const firstPath = parentPath[0];
    const rest = parentPath.slice(1);
    let registerPath = from.register;

    const historyPaths = {
      professional: 'Professional',
      address: 'Address',
      person: 'Person',
      company: 'Company',
    };

    let historyPath = from.history[historyPaths[firstPath]]
      ? from.history[historyPaths[firstPath]][0]
      : null;

    if (historyPath !== null) {
      rest.forEach((path) => {
        historyPath = historyPath[path];
      });
    }

    parentPath.forEach((path) => {
      registerPath = registerPath[path];
    });

    return historyPath
      ? historyPath[key]
      : registerPath
      ? registerPath[key]
      : null;
  }
}

// Utilitários para migração V2 → V3 Companies API
export class CompanyApiUtils {
  /**
   * Converte status da API V3 (inglês) para exibição (português)
   * @param status Status da API: 'active' | 'inactive'
   * @param type Tipo: 'company' retorna 'Ativa'/'Inativa', 'general' retorna 'Ativo'/'Inativo'
   */
  static getStatusDisplay(status: string, type: 'company' | 'general' = 'general'): string {
    if (status === 'active') {
      return type === 'company' ? 'Ativa' : 'Ativo';
    }
    if (status === 'inactive') {
      return type === 'company' ? 'Inativa' : 'Inativo';
    }
    return status; // Fallback
  }

  /**
   * Verifica se o status é inativo
   */
  static isStatusInactive(status: string): boolean {
    return status === 'inactive';
  }

  /**
   * Converte datetime ISO para formato brasileiro (DD/MM/YYYY HH:MM)
   */
  static formatDateBR(isoDate: string | null | undefined): string {
    if (!isoDate) return '';

    try {
      const date = new Date(isoDate);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return isoDate; // Fallback
    }
  }

  /**
   * Converte datetime ISO para formato brasileiro apenas data (DD/MM/YYYY)
   */
  static formatDateOnlyBR(isoDate: string | null | undefined): string {
    if (!isoDate) return '';

    try {
      const date = new Date(isoDate);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      return isoDate; // Fallback
    }
  }
}

export class GetHelper {
  refObject: any;
  newObject: any;

  constructor(object: any) {
    this.refObject = { ...object };
    this.newObject = { ...object };
  }

  get(key: string) {
    if (this.refObject.hasOwnProperty(key)) {
      if (this.newObject.hasOwnProperty(key)) {
        delete this.newObject[key];
      }

      return this.refObject[key];
    }
  }

  getRemainingObject() {
    return { ...this.newObject };
  }
}

/**
 * Normaliza um valor para comparação (undefined -> null, ordena chaves de objetos).
 * Usado para comparar payloads e evitar PATCH/PUT/POST quando nada mudou.
 */
function normalizePayloadForCompare(obj: any): any {
  if (obj === undefined) return null;
  if (obj === null) return null;
  if (Array.isArray(obj)) return obj.map(normalizePayloadForCompare);
  if (typeof obj === 'object') {
    const out: Record<string, any> = {};
    for (const k of Object.keys(obj).sort()) {
      const v = normalizePayloadForCompare(obj[k]);
      out[k] = v === undefined ? null : v;
    }
    return out;
  }
  return obj;
}

/**
 * Compara dois payloads (objetos) de forma que sejam considerados iguais
 * mesmo com undefined/null ou ordem de chaves diferente.
 * Útil para evitar requisição de save quando os dados da etapa não mudaram.
 */
export function payloadsEqual(
  a: Record<string, any> | null | undefined,
  b: Record<string, any> | null | undefined
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return (
    JSON.stringify(normalizePayloadForCompare(a)) ===
    JSON.stringify(normalizePayloadForCompare(b))
  );
}
